'use client';

import { useEffect, useRef } from 'react';
import { Phone, PhoneOff, User } from 'lucide-react';
import { Modal } from '@/shared/ui/Modal';
import { useStaffCallStore } from '../hooks/useStaffCallClient';

interface IncomingCallModalMobileProps {
  onAccept: () => void;
  onDecline: () => void;
}

export function IncomingCallModalMobile({
  onAccept,
  onDecline,
}: IncomingCallModalMobileProps) {
  const store = useStaffCallStore();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isOpen = store.state === 'incoming' && !!store.incomingCall;

  useEffect(() => {
    // Captured once, and that matters: by the time cleanup runs React may have
    // already detached the element and nulled the ref, so reading
    // `audioRef.current` there can silently skip the pause — leaving the
    // ringtone playing on a phone after the call has been answered.
    const audio = audioRef.current;
    if (isOpen) {
      audio?.play().catch(() => {});
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([400, 200, 400, 200, 400]);
      }
    }
    return () => {
      audio?.pause();
      if (audio) audio.currentTime = 0;
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(0);
      }
    };
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onDecline}
      title=""
      fullScreen
      mobileSheet={false}
      id="calls_incoming_modal"
    >
      <div className="flex flex-col items-center justify-between h-full py-12 px-6">
        <div className="flex flex-col items-center gap-6">
          {/* Avatar */}
          <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-hms-card border-2 border-hms-border animate-pulse">
            <User className="w-10 h-10 text-hms-gold" />
            <span className="absolute inset-0 rounded-full border-2 border-hms-gold/30 animate-ping" />
          </div>

          {/* Caller info */}
          <div className="text-center">
            <p className="text-2xl font-semibold text-hms-text">
              {store.incomingCall?.guestName ?? 'Guest'}
            </p>
            <p className="text-base text-hms-textSub mt-1">
              {store.incomingCall?.hotelName ?? 'Hotel'}
            </p>
            <p className="text-sm text-hms-textDim mt-1">
              Booking {store.incomingCall?.bookingId}
            </p>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-hms-green/10 text-hms-green text-sm font-medium">
            <span className="w-2 h-2 rounded-full bg-hms-green animate-pulse" />
            Incoming Call
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-6 w-full max-w-xs">
          <button
            id="calls_incoming_decline_btn_mobile"
            type="button"
            onClick={onDecline}
            className="flex-1 flex flex-col items-center gap-2 py-4 rounded-2xl bg-hms-red/10 text-hms-red"
          >
            <PhoneOff className="w-8 h-8" />
            <span className="text-sm font-medium">Decline</span>
          </button>
          <button
            id="calls_incoming_accept_btn_mobile"
            type="button"
            onClick={onAccept}
            className="flex-1 flex flex-col items-center gap-2 py-4 rounded-2xl bg-hms-green text-white"
          >
            <Phone className="w-8 h-8" />
            <span className="text-sm font-medium">Accept</span>
          </button>
        </div>
      </div>

      <audio ref={audioRef} loop preload="auto" src="/sounds/incoming-call.mp3" />
    </Modal>
  );
}
